import { ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import { Form } from 'react-bootstrap';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useFetchOperation } from '../../service/Operation';
import { useAppSelector } from '../../store';
import { Card, DictionarySlim } from '../../types/Common';
import AutoPaginationTable from '../general/AutoPaginationTable';
import Error from '../general/Error';
import Loader from '../general/Loader';
import DicSelectPanel from './DicSelectPanel';
import SearchPanel from './SearchPanel';
import { getWiktionaryUrl } from './../card/ExternalLinksDropdown';
import PhraseColoring from './PhraseColoring';

interface FoundCard extends Card {
    phraseWiktionaryLanguageName: string | undefined,
    wiktionaryUrl: string | undefined,
}

export default function SearchCards() {
    const lastDictionaryId = useAppSelector(state => state.training.card?.modeInfo?.dictionaryId);

    const location = useLocation();
    const state = location.state as { text: string | undefined, full: boolean | undefined };
    const params = useParams();

    const [text, setText] = React.useState(state?.text ?? '');
    const [full, setFull] = React.useState(state?.full ?? false);
    const [dictionaryId, setDictionaryId] = React.useState<number>(params.dictionaryId ? parseInt(params.dictionaryId) : lastDictionaryId ?? 2);
    const [dictionaries, setDictionaries] = React.useState<DictionarySlim[]>([]);

    const [cards, setCards] = React.useState<FoundCard[]>();

    const [, startGetting] = useFetchOperation(onDictionariesFetched, undefined, true);
    const [search, startSearch] = useFetchOperation(setData);

    React.useEffect(() => {
        startGetting('get', `api/dictionaries/slim/`)
        if (state) {
            onSearch(state.text ?? '', state.full ?? false);
        }
        document.title = `Search cards - ${(document as any).rootTitle}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function setData(data: FoundCard[]) {
        data.forEach(x => {
            if (x.phraseWiktionaryLanguageName)
                x.wiktionaryUrl = getWiktionaryUrl(x.phrase, x.phraseWiktionaryLanguageName);
        })
        setCards(data);
    }

    function onDictionariesFetched(data: DictionarySlim[]) {
        setDictionaries(data);
    }

    async function onSearch(text: string, full: boolean) {
        setCards([]);
        setText(text);
        setFull(full);
        startSearch('get', `api/cards/search/${dictionaryId}?text=${text}&extended=${full}`);
        document.title = `${text} - Search cards - ${(document as any).rootTitle}`;
    }

    function onDictionaryChange(value: number) {
        setDictionaryId(value);
        window.history.replaceState(null, "", window.location.pathname.replace(/\d+/, value.toString()));
    }

    return (
        <>
            <h1>Search cards</h1>
            <Form className="width-limit">
                <SearchPanel
                    text={text}
                    full={full}
                    onSearch={onSearch}
                    autoFocus={!state} />
                <DicSelectPanel
                    dictionaries={dictionaries}
                    dictionaryId={dictionaryId}
                    onDictionaryChange={onDictionaryChange} />
            </Form>
            {
                search.active ?
                    <Loader />
                    :
                    search.error ?
                        <Error text={search.error} />
                        :
                        cards ?
                            cards.length ?
                                <AutoPaginationTable className="mb-3"
                                    columns={full ? extendedColumns : simpleColumns}
                                    data={cards} />
                                :
                                <p className="my-3">No cards found</p>
                            :
                            <></>
            }
        </>
    );
}

const simpleColumns: ColumnDef<FoundCard>[] = [
    {
        header: '#',
        accessorFn: x => x.id,
        cell: props => <Link to={`/cards/${props.row.original.id}`}> {props.row.index + 1}</Link>
    },
    {
        header: 'Phrase',
        accessorFn: x => x.phrase,
        cell: props => <PhraseColoring phrase={props.row.original.phrase} phraseColoring={props.row.original.phraseColoring} />
    },
    {
        header: 'Meaning',
        accessorFn: x => x.meaning,
        cell: props => props.row.original.wiktionaryUrl ? <a target="_blank" href={props.row.original.wiktionaryUrl}>{props.row.original.meaning}</a> : <>{props.row.original.meaning}</>
    },
];

const extendedColumns: ColumnDef<FoundCard>[] = [
    {
        header: '#',
        accessorFn: x => x.id,
        cell: props => <Link to={`/cards/${props.row.original.id}`}> {props.row.index + 1}</Link>
    },
    {
        header: 'Phrase',
        accessorFn: x => x.phrase,
        cell: props => <PhraseColoring phrase={props.row.original.phrase} phraseColoring={props.row.original.phraseColoring} />
    },
    {
        header: 'Meaning',
        accessorFn: x => x.meaning,
        cell: props => props.row.original.wiktionaryUrl ? <a target="_blank" href={props.row.original.wiktionaryUrl}>{props.row.original.meaning}</a> : <>{props.row.original.meaning}</>
    },
    {
        header: 'Pronunciation',
        accessorFn: x => x.pronunciation,
    },
    {
        header: 'Forms',
        accessorFn: x => x.forms,
    },
    {
        header: 'Example',
        accessorFn: x => x.example,
    },
];
